import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom'; // Removed useLocation

const AuthContext = createContext(null);

const isTokenExpired = (token) => {
    if (!token) return true;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return Date.now() >= payload.exp * 1000;
    } catch (e) { return true; }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            if (isTokenExpired(parsedUser.token)) {
                localStorage.removeItem('user');
                setUser(null);
            } else {
                setUser(parsedUser);
            }
        }
        setLoading(false);
    }, []);

    // 1. Memoized logout function
    const logout = useCallback((message = null) => {
        setUser(null);
        localStorage.removeItem('user');
        if (message && typeof message === 'string') {
            navigate('/login', { state: { sessionMessage: message } });
        } else {
            navigate('/login');
        }
    }, [navigate]);

    useEffect(() => {
        if (!user || !user.token) return;

        const intervalId = setInterval(() => {
            if (isTokenExpired(user.token)) {
                logout("Your session has expired for security reasons. Please log in again.");
            }
        }, 60000);

        return () => clearInterval(intervalId);
    }, [user, logout]); // Added logout here safely

    const updateUserInfo = (newData) => {
        setUser(prevUser => {
            const updatedUser = { ...prevUser, ...newData };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            return updatedUser;
        });
    };

    const login = async (username, password) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Login failed.');
            }

            const data = await response.json();

            const userData = {
                userId: data.userId,
                username: data.username,
                role: data.role,
                token: data.accessToken,
                isFirstLogin: data.isFirstLogin,
                profilePicture: data.profilePicture,
                status: data.status
            };

            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));

            if (userData.isFirstLogin) {
                navigate('/complete-profile');
            } else {
                navigate('/');
            }

            return { success: true };
        } catch (error) {
            return { success: false, message: error.message };
        }
    };

    const value = { user, login, logout, updateUserInfo, loading };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};